/**
 * 🔴 Ticket 74 (D2) — THE ONE HOME of the server-owned key list and the two
 * helpers built on it.
 *
 * They lived in `EditListingPhotosPanel/EditListingPhotosPanel.js` and moved
 * here byte-identical — the one addition is Ticket 74's nine keys, labelled
 * where they sit in the list — so `EditListingPage.duck.js` can import the
 * list for the central save filter (`omitSystemOwnedPublicData`, below)
 * without importing a React panel module. A duck importing a panel invites an
 * import cycle and pulls component code into the store. The panel re-exports all four names, so
 * every existing import keeps working.
 *
 * This module is PURE: no React, no SDK, no store.
 */

// 🔴 Story 15.8 (AC-2 / B-3) — publicData keys the SERVER owns.
//
// `adRenderedState` is written by tph-api's ad-render processor, minutes after
// the seller's save. A panel's publicData spread (the Photos panel's, where
// this note was first written) is a PAGE-LOAD SNAPSHOT (`publicData` is
// captured at panel render, not re-read at submit), so spreading it back would
// REVERT the server's value. The next `listing/updated` would then see
// `current cover ≠ adRenderedState.coverImageId` and enqueue a re-render for a
// save that changed nothing about the ad — once per save for the rest of the
// page session, each costing a fresh Airtable attachment id and a Dropbox
// re-upload. Unlike a render race this does NOT decay: the snapshot stays stale
// until reload.
//
// `sdk.listings.update` MERGES publicData, so OMITTING a key is a no-op for it,
// not a clear. Do not "simplify" this away.
//
// (`autoAdImageUrl` / `autoSoldAdImageUrl` have the identical staleness and are
// currently harmless only because they are trigger-excluded; they are included
// here because it costs nothing.)
export const SERVER_OWNED_PUBLIC_DATA_KEYS = [
  'adRenderedState',
  'autoAdImageUrl',
  'autoSoldAdImageUrl',
  // 🔴 Story 11.17 — the Cloudflare Stream bookkeeping map, keyed on objectKey
  // and written ONLY by tph-api's seller-video-stream cron.
  //
  // It lives here, as its own top-level key, rather than as a sixth key on each
  // `additionalVideoUploads` entry — which is the obvious design and is wrong
  // twice over. `cleanAdditionalVideoUploads` rebuilds every entry from a
  // five-key allowlist (objectKey, filename, contentType, sizeBytes,
  // uploadedAt), so a sixth key is DISCARDED on the seller's next save with no
  // error anywhere. Whitelisting a sixth key there would still lose the write
  // to a stale-form race: getInitialValues seeds from publicData at render and
  // the seller saves minutes later, which is exactly the window after an
  // upload. Omitting the key here means the client never writes it, so no race
  // exists.
  'sellerVideoStream',
  // 🔴 Story 11.19 (ledger #1514) — the provenance stamp tph-api Story 11.18
  // writes: a flat array of the 11-character YouTube ids of every video WE
  // produced for this listing, in production order, no duplicates. Written
  // only by tph-api's `videoUrlWriteback`, in the same update as the
  // `additionalVideos` line it places. The wizard reads it to label a link row
  // as ours; it never writes it. Until this entry existed a seller save spread
  // the page-load snapshot back and could overwrite the stamp with an older
  // copy, which is the same stale-form race the `sellerVideoStream` note above
  // describes.
  'producedVideoIds',
  // 🔴 Story 12.23 (cold review 2026-09-14, MEDIUM-3) — the finished video ad's
  // URL, written by tph-api's ad-video ingest and cleared by its PNG re-render
  // once she moves off video. Spreading the page-load snapshot back would restore
  // a marker the server had just cleared, and a later video pick would then be
  // handed the OLD video at once while tph-api's recovery trigger stayed silent.
  'autoAdVideoUrl',
  // 🔴 Story 16.2 (ID-7) — Story 16-1's YouTube write-back: a map keyed on
  // objectKey, `{ videoId, public, blockedEverywhere, publishedAt }`, written
  // ONLY by tph-api's youtube-publish cron and reconciled on every run. The
  // listing page reads it for "Copy video link" and `VideoObject`
  // (`src/util/listingVideoItems.js`). This entry must be live in production
  // BEFORE that writer is enabled there (16-1 D7): without it a wizard save
  // would spread a stale copy back over the cron's write.
  'sellerVideoYoutube',
  // 🔴 Ticket 74 (D3, ledger #694) — the rest of what tph-api writes at runtime
  // and no wizard step writes, from a full enumeration of tph-api's
  // `listings.update` writers (tph-api `origin/staging` 898af17fd). Before these
  // were here, a wizard tab opened before the horse was marked sold or put on
  // hold put it back to Active on its next save. Writers, by key:
  //   listingStatus, holdReason, holdDate — routes/listingStatusCapture.js
  //     (sold / on hold / reactivate) and services/listingTakedown.js
  //   searchRank — the same two, the activity-stamp consumer and the nightly
  //     search-rank sweep
  //   lastActivityAt — the activity-stamp consumer, the QA-approved webhook and
  //     the Stripe paid-invoice handler (sorts the homepage Newest grid)
  //   papersOnFile — the papers-provenance consumer
  //   proseTags, proseTagsSuppressed — the listing-tag processor, tag check and
  //     the tag-suppression operator command
  //   adImageUrl — the Story 7.2 ad-image regen write-back
  // `city` and `contactPhone` are not here: the save sanitizer never sends them
  // (`PRIVATE_ONLY_PUBLIC_DATA_KEYS`, util/locationPrivacy.js). `listingTier`
  // is not here: the Details step writes it on a new draft — see
  // `omitSystemOwnedPublicData` below.
  'listingStatus',
  'holdReason',
  'holdDate',
  'searchRank',
  'lastActivityAt',
  'papersOnFile',
  'proseTags',
  'proseTagsSuppressed',
  'adImageUrl',
];

// 🔴 Story 16.0 Part A (A-D1) — EXPORTED, and this is the ONLY definition.
//
// It lived as three byte-identical module-private copies (here, Pricing, Social
// Post) while the Details and Location panels spread the raw snapshot and had
// none. Location is the LAST wizard tab, so its save runs immediately before
// `publishDraft`: publishing reverted the seller-video cron's clip state on 2 of
// 2 listings inspected, and the seller's clips were invisible for up to 4m44s
// (ledger #2559).
//
// 🔴 IF YOU ADD A WIZARD PANEL THAT SPREADS THE LISTING'S `publicData` INTO ITS
// SAVE PAYLOAD, SPREAD `omitServerOwnedKeys(publicData)` — NEVER THE RAW
// SNAPSHOT. `serverOwnedKeys.guard.test.js` fails, naming your file, if you
// forget; it enumerates the panel directory so a new panel is caught with no
// list to remember to update.
export const omitServerOwnedKeys = publicData =>
  Object.keys(publicData || {}).reduce((acc, key) => {
    if (!SERVER_OWNED_PUBLIC_DATA_KEYS.includes(key)) {
      acc[key] = publicData[key];
    }
    return acc;
  }, {});

// 🔴 Story 12.28 (cold review 2026-09-18, HIGH-2) — keys that are SANITIZED on
// the way out, so re-sending the stored copy is not a no-op but a DESTRUCTIVE
// rewrite.
//
// `publicData.location` is the whole list today. The listing's stored address is
// the exact street address on any listing that predates the 12.28 backfill. A
// panel that spreads the page-load snapshot forward re-sends it, and the save
// thunk's sanitizer then coarsens it and shallow-merges the coarse value over
// the stored one. Nothing captured the exact address on the way past, so it is
// gone — and gone from the backfill's recovery evidence with it (AC6(ii)).
//
// The trigger was a seller editing her listing TITLE on Step 1 and saving. The
// Location tab was never opened.
//
// 🔴 THIS IS NOT THE SAME LIST AS SERVER_OWNED_PUBLIC_DATA_KEYS AND MUST NOT BE
// MERGED INTO IT. A server-owned key is one NO panel writes. `location` is
// written — by `EditListingLocationPanel`, on a fresh pick, explicitly, after
// this helper has removed the stale copy.
export const SANITIZED_PUBLIC_DATA_KEYS = ['location'];

/**
 * 🔴 THE HELPER EVERY PANEL SPREADS. Drops both lists.
 *
 * `omitServerOwnedKeys` is kept exported because the 16.0a delivery tests
 * iterate its list, but no panel should call it directly: a panel that wants the
 * snapshot wants it without the location too.
 */
export const omitCarriedForwardKeys = publicData =>
  Object.keys(publicData || {}).reduce((acc, key) => {
    if (!SERVER_OWNED_PUBLIC_DATA_KEYS.includes(key) && !SANITIZED_PUBLIC_DATA_KEYS.includes(key)) {
      acc[key] = publicData[key];
    }
    return acc;
  }, {});

/**
 * 🔴 Ticket 74 (D1, D4) — THE CENTRAL SAVE FILTER. Every wizard save of an
 * existing listing passes its `publicData` through this in the save thunk
 * (`EditListingPage.duck.js`), whatever the panel built.
 *
 * Each panel's `publicData` is the page-load copy, and a panel may carry it
 * forward. A key the system changed since the page loaded — sold, on hold, a
 * paid upgrade, last-active, an ad image — would go back to its old value. So:
 *
 * - every key in `SERVER_OWNED_PUBLIC_DATA_KEYS` is dropped, always. No wizard
 *   step writes any of them, and Sharetribe MERGES publicData, so a dropped key
 *   is left as the system last wrote it (proven on staging by #2559's step 8);
 * - `listingTier` is dropped when the STORED listing already has a tier. The
 *   Details step writes it on a new draft (the seller's pick), but once a tier
 *   exists it only ever re-sends the stored one (`existingListingTier ||
 *   values.listingTier`), so on an existing tier the write is a page-load copy
 *   that would undo tph-api's upgrade. A draft's tier change goes through
 *   `setDraftListingTierThunk`, which is not filtered. No stored listing (or no
 *   stored tier): the tier is sent, exactly as before.
 *
 * Keys both sides write (`location`, `additionalVideos`) are not this filter's
 * to own — ledger #3402.
 *
 * Absent, not undefined: the output never carries a dropped key at all.
 *
 * @param {Object} [publicData] the outgoing publicData
 * @param {Object} [storedPublicData] the listing's publicData in the store at save time
 * @returns {Object|undefined} publicData without system-owned keys; a non-object passes through
 */
export const omitSystemOwnedPublicData = (publicData, storedPublicData) => {
  if (!publicData || typeof publicData !== 'object' || Array.isArray(publicData)) {
    return publicData;
  }
  const storedHasTier = !!storedPublicData?.listingTier;
  return Object.keys(publicData).reduce((acc, key) => {
    if (SERVER_OWNED_PUBLIC_DATA_KEYS.includes(key)) return acc;
    if (key === 'listingTier' && storedHasTier) return acc;
    acc[key] = publicData[key];
    return acc;
  }, {});
};
